import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useEffect, useMemo, useState } from "react";
import LabPriceInputs from "./LabPriceInputs";
import {
  type CatalogCategory,
  type PriceListRow,
  type Prices,
  MISSING_ITEM_HINT,
  breakdownLabel,
  chartTeethCount,
  emptyPrices,
  findCatalogItem,
  groupRows,
  priceSummary,
} from "./labCatalog";
import {
  type InvoiceLineForm,
  addLine,
  applyChartTeeth,
  lineFromCatalog,
  lineFromRow,
  lineNetPrice,
  linePriceMissing,
  lineUnit,
} from "./labOrderFormUtils";

const lkr = (n: number) => `LKR ${(Number(n) || 0).toLocaleString("en-LK")}`;
const fieldClass =
  "h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100";
const labelClass = "mb-1.5 block text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400";

type Pick =
  | { kind: "row"; row: PriceListRow }
  | { kind: "catalog"; catalogItemId: string }
  | null;

/**
 * The order's lab items: one dropdown (grouped by category, no prices in
 * it) and the price box(es), then Add. A registered lab's price fills in and
 * is locked; a hand-added lab's price is the clinic's to type (blank = add it
 * later). Hand-added labs can also order a catalogue item that isn't on
 * their list yet — it joins the list when the order is saved.
 */
export default function LabOrderItemsEditor({
  registered,
  labName,
  priceList,
  priceListLoading,
  catalog,
  toothNos,
  lines,
  onChange,
  disabled = false,
  onlyCategoryId = "",
}: {
  registered: boolean;
  labName?: string;
  priceList: PriceListRow[];
  priceListLoading: boolean;
  catalog: CatalogCategory[];
  toothNos: string[];
  lines: InvoiceLineForm[];
  onChange: (lines: InvoiceLineForm[]) => void;
  disabled?: boolean;
  /** Offer only this master category (a treatment stage's lab category) until "Show all" */
  onlyCategoryId?: string;
}) {
  const [pick, setPick] = useState<Pick>(null);
  const [prices, setPrices] = useState<Prices>(emptyPrices());
  const [showCatalog, setShowCatalog] = useState(false);
  const [error, setError] = useState("");
  const [showAllCategories, setShowAllCategories] = useState(false);
  const chartTeeth = chartTeethCount(toothNos);
  const restricted = !!onlyCategoryId && !showAllCategories;
  const shownRows = useMemo(
    () => (restricted ? priceList.filter((r) => r.categoryId === onlyCategoryId) : priceList),
    [priceList, restricted, onlyCategoryId]
  );
  const groups = useMemo(() => groupRows(shownRows), [shownRows]);
  const restrictedName = catalog.find((c) => c.id === onlyCategoryId)?.name;

  // Base + per tooth lines follow the tooth chart until a number is typed.
  useEffect(() => {
    const next = applyChartTeeth(lines, chartTeeth);
    if (next !== lines) onChange(next);
  }, [chartTeeth, lines, onChange]);

  const onList = useMemo(() => new Set(priceList.map((r) => r.catalogItemId).filter(Boolean) as string[]), [priceList]);
  const pickedMethod =
    pick?.kind === "row"
      ? pick.row.pricingMethod
      : pick?.kind === "catalog"
        ? findCatalogItem(catalog, pick.catalogItemId)?.item.pricingMethod ?? "flat"
        : "flat";

  function choose(value: string) {
    setError("");
    if (!value) {
      setPick(null);
      setPrices(emptyPrices());
      return;
    }
    if (value.startsWith("cat:")) {
      setPick({ kind: "catalog", catalogItemId: value.slice(4) });
      setPrices({ ...emptyPrices(), includedTeeth: 0 });
      return;
    }
    const row = priceList.find((r) => r.id === value);
    if (!row) return;
    setPick({ kind: "row", row });
    setPrices({ ...emptyPrices(), ...row.prices });
  }

  function add() {
    if (!pick) {
      setError("Pick an item first");
      return;
    }
    let line: InvoiceLineForm;
    if (pick.kind === "row") {
      line = lineFromRow(pick.row, { registered, chartTeeth });
      if (!registered) line = { ...line, prices };
    } else {
      const found = findCatalogItem(catalog, pick.catalogItemId);
      if (!found) return;
      line = { ...lineFromCatalog(found.item, found.category, chartTeeth), prices };
    }
    onChange(addLine(lines, line));
    setPick(null);
    setPrices(emptyPrices());
    setShowCatalog(false);
  }

  const update = (key: string, patch: Partial<InvoiceLineForm>) =>
    onChange(lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const selectValue = pick?.kind === "row" ? pick.row.id : pick?.kind === "catalog" ? `cat:${pick.catalogItemId}` : "";
  const notOnList = catalog
    .filter((c) => !restricted || c.id === onlyCategoryId)
    .map((c) => ({ ...c, items: c.items.filter((i) => !onList.has(i.id)) }))
    .filter((c) => c.items.length);

  if (priceListLoading) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-6 text-sm text-slate-500 dark:border-slate-600">
        <span className="size-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        Loading price list…
      </div>
    );
  }

  if (registered && priceList.length === 0) {
    return (
      <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
        {labName || "This lab"} has no priced items yet. Ask them to add items in their portal, or pick another lab.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {!disabled ? (
        <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4 dark:border-slate-600 dark:bg-slate-800/50">
          <p className="text-xs text-slate-500">Pick an item, check the price, add.</p>
          <div>
            <label htmlFor="lab-order-item" className={labelClass}>
              Item <span className="text-primary">*</span>
            </label>
            <select id="lab-order-item" value={selectValue} onChange={(e) => choose(e.target.value)} className={fieldClass}>
              <option value="">Choose an item…</option>
              {groups.map((g) => (
                <optgroup key={g.name} label={g.name}>
                  {g.rows.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                      {r.catalogName && r.catalogName !== r.name ? ` (${r.catalogName})` : ""}
                    </option>
                  ))}
                </optgroup>
              ))}
              {!registered && showCatalog
                ? notOnList.map((c) => (
                    <optgroup key={`cat-${c.id}`} label={`${c.name} — not on the price list yet`}>
                      {c.items.map((i) => (
                        <option key={i.id} value={`cat:${i.id}`}>
                          {i.name}
                        </option>
                      ))}
                    </optgroup>
                  ))
                : null}
            </select>
            {onlyCategoryId ? (
              <p className="mt-1.5 text-xs text-slate-500">
                {restricted ? (
                  <>
                    Showing {restrictedName || "this stage's category"} only.{" "}
                    <button type="button" onClick={() => setShowAllCategories(true)} className="font-medium text-primary hover:underline">
                      Show all categories
                    </button>
                  </>
                ) : (
                  <button type="button" onClick={() => setShowAllCategories(false)} className="font-medium text-primary hover:underline">
                    Only {restrictedName || "this stage's category"}
                  </button>
                )}
              </p>
            ) : null}
            {!registered ? (
              <p className="mt-1.5 text-xs text-slate-500">
                {showCatalog ? (
                  <>Items from the catalogue are added to {labName || "this lab"}&apos;s price list when you save. {MISSING_ITEM_HINT}</>
                ) : (
                  <button type="button" onClick={() => setShowCatalog(true)} className="font-medium text-primary hover:underline">
                    Not in the price list?
                  </button>
                )}
              </p>
            ) : null}
          </div>

          {pick ? (
            registered && pick.kind === "row" ? (
              <div className="rounded-lg border border-primary/20 bg-primary/5 px-3 py-2.5 text-sm dark:bg-primary/10">
                <span className="font-semibold tabular-nums text-primary">{priceSummary(pick.row.pricingMethod, pick.row.prices)}</span>
                <span className="ml-2 text-xs text-slate-500">set by {labName || "the lab"}</span>
              </div>
            ) : (
              <LabPriceInputs method={pickedMethod} value={prices} onChange={setPrices} />
            )
          ) : null}

          {pick && pickedMethod === "base_plus_tooth" ? (
            <p className="text-xs text-slate-500">
              {chartTeeth
                ? `${chartTeeth} teeth from the chart.`
                : "Pick the teeth on the chart, or type the number of teeth on the line."}
            </p>
          ) : null}

          {error ? (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300" role="alert">
              {error}
            </p>
          ) : null}

          <button
            type="button"
            onClick={add}
            disabled={!pick}
            className="inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-primary to-primaryLight text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-45"
          >
            <AddIcon sx={{ fontSize: 18 }} />
            Add item
          </button>
        </div>
      ) : null}

      {lines.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-6 text-center text-sm text-slate-500 dark:border-slate-600 dark:bg-slate-800/40">
          No items added yet
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
          <table className="w-full min-w-[560px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/80 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-700">
                <th className="px-3 py-2">Item</th>
                <th className="w-20 px-3 py-2">Qty</th>
                <th className="w-20 px-3 py-2">Teeth</th>
                <th className="px-3 py-2">Price</th>
                <th className="px-3 py-2 text-right">Total</th>
                <th className="w-12 px-3 py-2">
                  <span className="sr-only">Remove</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const tooth = l.pricingMethod === "base_plus_tooth";
                return (
                  <tr key={l.key} className="border-b border-slate-100 align-top last:border-b-0 dark:border-slate-800">
                    <td className="px-3 py-2.5">
                      <p className="font-medium text-slate-900 dark:text-slate-100">{l.item}</p>
                      <p className="text-xs text-slate-500">
                        {l.catalogName !== l.item ? `${l.catalogName} · ` : ""}
                        {l.categoryName}
                        {!l.labItemId && !l.localItemId ? " · joins the price list on save" : ""}
                      </p>
                    </td>
                    <td className="px-3 py-2.5">
                      <input
                        type="number"
                        min={1}
                        max={99}
                        aria-label={`Quantity of ${l.item}`}
                        value={l.quantity}
                        disabled={disabled}
                        onChange={(e) => update(l.key, { quantity: Math.max(1, Math.min(99, Number(e.target.value) || 1)) })}
                        className={`${fieldClass} w-16`}
                      />
                    </td>
                    <td className="px-3 py-2.5">
                      {tooth ? (
                        <input
                          type="number"
                          min={1}
                          max={32}
                          aria-label={`Teeth for ${l.item}`}
                          value={l.teeth ?? ""}
                          disabled={disabled}
                          onChange={(e) => update(l.key, { teeth: Number(e.target.value) || null, teethAuto: false })}
                          className={`${fieldClass} w-16`}
                        />
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {l.priceLocked || disabled ? (
                        <span className="tabular-nums text-slate-700 dark:text-slate-200">
                          {tooth ? priceSummary(l.pricingMethod, l.prices) : lkr(lineUnit(l))}
                        </span>
                      ) : tooth ? (
                        <LabPriceInputs method={l.pricingMethod} value={l.prices} onChange={(p) => update(l.key, { prices: p })} />
                      ) : (
                        <input
                          type="number"
                          min={0}
                          aria-label={`Price of ${l.item}`}
                          value={l.prices.price ?? ""}
                          onChange={(e) =>
                            update(l.key, {
                              prices: { ...l.prices, price: e.target.value === "" ? null : Math.max(0, Number(e.target.value) || 0) },
                            })
                          }
                          placeholder="Later"
                          className={`${fieldClass} w-28`}
                        />
                      )}
                      {tooth && l.teeth ? (
                        <span className="mt-1 block text-xs text-slate-500">{breakdownLabel(l.pricingMethod, l.prices, l.teeth)}</span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-slate-900 dark:text-slate-100">
                      {linePriceMissing(l) ? <span className="text-xs font-normal text-slate-500">Price later</span> : lkr(lineNetPrice(l))}
                    </td>
                    <td className="px-3 py-2.5">
                      {!disabled ? (
                        <button
                          type="button"
                          onClick={() => onChange(lines.filter((x) => x.key !== l.key))}
                          className="flex size-8 items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40"
                          aria-label={`Remove ${l.item}`}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
